/// <reference types="../vite-env.d.ts" />
import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { TeamRole } from '../types/supabase'
import { checkUserTeamAccess } from '../lib/supabase'
import { supabase } from '../lib/supabase'
import TeamDashboard from './TeamDashboard'
import SkeletonLoader from '../components/SkeletonLoader'

const Dashboard: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>()
  const navigate = useNavigate()
  const { user, loading: authLoading, signOut } = useAuth()
  
  const [access, setAccess] = useState<{
    hasAccess: boolean
    role?: TeamRole
    loading: boolean
    error?: string
  }>({ hasAccess: false, loading: true })

  // Check for session tokens in URL fragment on component mount
  useEffect(() => {
    const handleUrlFragment = async () => {
      const fragment = window.location.hash.substring(1)
      console.log('Dashboard: Checking URL fragment:', fragment)
      
      if (fragment) {
        const params = new URLSearchParams(fragment)
        const accessToken = params.get('access_token')
        const refreshToken = params.get('refresh_token')
        
        console.log('Dashboard: Fragment tokens:', { 
          hasAccessToken: !!accessToken, 
          hasRefreshToken: !!refreshToken 
        })
        
        if (accessToken && refreshToken) {
          console.log('Dashboard: Found session tokens in URL fragment, setting up session...')
          try {
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            })
            
            if (error) {
              console.error('Dashboard: Error setting session:', error)
            } else {
              console.log('Dashboard: Session set successfully:', data.session?.user?.email)
              // Clear the URL fragment after successful session setup
              window.location.hash = ''
            }
          } catch (error) {
            console.error('Dashboard: Error processing session tokens:', error)
          }
        }
      }
    }

    handleUrlFragment()
  }, [])

  useEffect(() => {
    const checkAccess = async () => {
      console.log('=== DASHBOARD COMPONENT LOADING ===')
      console.log('Team ID from URL:', teamId)
      console.log('Current user:', user)
      console.log('Auth loading:', authLoading)
      
      // Wait for auth to finish loading
      if (authLoading) {
        console.log('Auth still loading, waiting...')
        return
      }
      
      if (!user) {
        console.log('No user found after auth loading completed')
        setAccess({ hasAccess: false, loading: false, error: 'User not authenticated' })
        return
      }
      
      if (!teamId) {
        console.log('No team ID provided')
        setAccess({ hasAccess: false, loading: false, error: 'No team ID provided' })
        navigate('/dashboard') // Redirect to user dashboard if no teamId
        return
      }

      try {
        console.log('Checking user access to team:', teamId)
        const { hasAccess, role } = await checkUserTeamAccess(teamId)
        console.log('Access check result:', { hasAccess, role })
        
        if (hasAccess) {
          setAccess({ hasAccess: true, role, loading: false })
        } else {
          setAccess({ hasAccess: false, loading: false, error: 'You do not have access to this team.' })
        }
      } catch (error: any) {
        console.error('Dashboard error:', error)
        setAccess({ hasAccess: false, loading: false, error: error.message || 'An error occurred while verifying access.' })
      }
    }

    checkAccess()
  }, [teamId, user, authLoading, navigate])
  
  const handleLogout = async () => {
    await signOut()
    navigate('/auth/login')
  }

  // Show loading while auth is loading OR while we're checking access
  if (authLoading || access.loading) {
    return <SkeletonLoader />
  }

  if (!access.hasAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-rose-500 text-xl mb-4">⚠️ Access Denied</div>
          <p className="text-slate-600 mb-4">{access.error}</p>
          <div className="space-x-4">
            <button 
              onClick={handleLogout}
              className="px-4 py-2 bg-slate-600 text-white font-semibold rounded-lg shadow-md hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-opacity-75"
            >
              Back to Login
            </button>
            <button 
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75"
            >
              Select a Different Team
            </button>
          </div>
        </div>
      </div>
    )
  }

  // If access is granted, render the team dashboard
  return <TeamDashboard teamId={teamId!} />
}

export default Dashboard 